import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Crown, Star, Sparkles, Save, ShieldCheck, KeyRound, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import TravelPreferencesSection from "../components/TravelPreferencesSection";

// Faded carousel of travel-evocative photos sitting behind the page chrome.
const CAROUSEL = [
  "https://images.unsplash.com/photo-1522383225653-ed111181a951?auto=format&fit=crop&w=2400&q=70", // Santorini
  "https://images.unsplash.com/photo-1503614472-8c93d56e92ce?auto=format&fit=crop&w=2400&q=70", // mountain lake
  "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=2400&q=70", // beach
  "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=2400&q=70", // tropical
  "https://images.unsplash.com/photo-1499591934245-40b55745b905?auto=format&fit=crop&w=2400&q=70", // alpine
  "https://images.unsplash.com/photo-1530541930197-ff16ac917b0e?auto=format&fit=crop&w=2400&q=70", // venice
];

function CarouselBackground() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % CAROUSEL.length), 6500);
    return () => clearInterval(t);
  }, []);
  return (
    <>
      <div className="fixed inset-0 -z-10 pointer-events-none" data-testid="membership-carousel" aria-hidden="true">
        {CAROUSEL.map((src, i) => (
          <div
            key={src}
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-[1800ms] ease-in-out"
            style={{
              backgroundImage: `url('${src}')`,
              opacity: i === idx ? 0.18 : 0,
            }}
          />
        ))}
        {/* Soft ivory wash so type stays readable. */}
        <div className="absolute inset-0 bg-[var(--ivory)]/55" />
      </div>
    </>
  );
}

export default function MembershipPage() {
  const [tiers, setTiers] = useState([]);
  const [loadingTier, setLoadingTier] = useState(null);
  const [pendingTier, setPendingTier] = useState(null);
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [signupBusy, setSignupBusy] = useState(false);
  const [signup, setSignup] = useState({ name: "", email: "", password: "" });

  useEffect(() => {
    api.get("/memberships").then((r) => setTiers(r.data.tiers));
    // If the user just returned from HubSpot, surface a "confirm membership" prompt
    try {
      const t = localStorage.getItem("sasha_pending_tier");
      if (t && user && !user.membership_tier) setPendingTier(t);
    } catch (_) { /* ignore */ }
  }, [user]);

  const claimMembership = async () => {
    try {
      await api.post("/me/claim-membership", { tier: pendingTier });
      try { localStorage.removeItem("sasha_pending_tier"); } catch (_) {}
      setPendingTier(null);
      await refresh();
      toast.success("Welcome — your membership is active.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not activate membership.");
    }
  };

  const checkout = async (tier) => {
    // By-appointment-only tiers — open the Zoom scheduler.
    if (tier.scheduler_url) {
      window.open(tier.scheduler_url, "_blank", "noopener,noreferrer");
      return;
    }
    // Free tier — no payment, send users straight to Sasha.
    if (tier.id === "free" || (!tier.payment_url && (tier.price === 0 || tier.price === null))) {
      navigate("/chat");
      return;
    }
    // Paid tiers — direct HubSpot payment link.
    if (tier.payment_url) {
      try {
        localStorage.setItem("sasha_pending_tier", tier.id);
      } catch (_) { /* ignore */ }
      window.location.href = tier.payment_url;
      return;
    }
    toast.error("Payment link unavailable.");
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24 relative" data-testid="membership-page">
      <CarouselBackground />
      {pendingTier && tiers.length > 0 && (
        <div className="lux-card-dark mb-10 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="claim-membership-banner">
          <div className="flex-1">
            <div className="overline text-[var(--gold)]">Welcome back from checkout</div>
            <div className="font-display text-3xl mt-1">
              Activate your <em className="text-[var(--gold)] not-italic">{tiers.find((t) => t.id === pendingTier)?.name || pendingTier}</em> membership
            </div>
            <p className="text-sm opacity-90 mt-2">
              If your HubSpot payment is complete, activate your membership to unlock travel preferences and your assigned concierge.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={claimMembership} className="btn-gold" data-testid="claim-confirm-btn">
              Activate now
            </button>
            <button
              onClick={() => {
                try { localStorage.removeItem("sasha_pending_tier"); } catch (_) {}
                setPendingTier(null);
              }}
              className="text-xs uppercase tracking-[0.18em] text-[var(--ivory)]/70 hover:text-[var(--ivory)]"
              data-testid="claim-dismiss-btn"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      <div className="text-center max-w-3xl mx-auto fade-up">
        <div className="overline">Club Janté</div>
        <h1 className="font-display text-5xl sm:text-6xl text-[var(--emerald)] mt-4">
          Choose your <em className="text-[var(--gold)]">altitude</em>.
        </h1>
        <p className="mt-6 text-lg text-[var(--ink-2)]">
          From elegant beginnings to white-glove signature journeys — Sasha plans, you arrive.
        </p>
      </div>

      {/* Free-account attributes + sign-up form
          Visible only to logged-out visitors. Lets them open a complimentary
          account so trips Sasha builds for them are persisted under their
          reference number — accessible the moment they return. */}
      {!user && (
        <section
          className="mt-16 grid lg:grid-cols-[1.1fr_1fr] gap-8 items-stretch"
          data-testid="free-membership-section"
        >
          {/* Attributes card */}
          <div className="lux-card flex flex-col">
            <div className="flex items-center gap-2">
              <Sparkles size={18} strokeWidth={1.25} className="text-[var(--gold)]" />
              <div className="overline">Begin with a free account</div>
            </div>
            <h2 className="font-display text-3xl text-[var(--emerald)] mt-3 leading-snug">
              Save every trip you imagine — recall it any time.
            </h2>
            <ul className="mt-6 space-y-3 text-sm text-[var(--ink-2)]">
              <li className="flex items-start gap-3">
                <Save size={15} strokeWidth={1.5} className="text-[var(--gold)] mt-0.5 shrink-0" />
                <div>
                  <div className="text-[var(--emerald)] font-display text-lg leading-tight">Unlimited trip storage</div>
                  Every itinerary saved under your trip reference. Pull it up by code, any time.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <KeyRound size={15} strokeWidth={1.5} className="text-[var(--gold)] mt-0.5 shrink-0" />
                <div>
                  <div className="text-[var(--emerald)] font-display text-lg leading-tight">Sasha remembers</div>
                  Preferences, past trips, dietary notes — carried across every visit.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <ShieldCheck size={15} strokeWidth={1.5} className="text-[var(--gold)] mt-0.5 shrink-0" />
                <div>
                  <div className="text-[var(--emerald)] font-display text-lg leading-tight">No commitment</div>
                  Free forever. Upgrade when you want concierge access, member-only rates, and unlimited changes.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Crown size={15} strokeWidth={1.5} className="text-[var(--gold)] mt-0.5 shrink-0" />
                <div>
                  <div className="text-[var(--emerald)] font-display text-lg leading-tight">A whisper of more</div>
                  Paid members never coordinate alone — concierge handles flights, cancellations, surprises. <span className="text-[var(--brand-red)]">See tiers below.</span>
                </div>
              </li>
            </ul>
          </div>

          {/* Sign-up form card */}
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (signupBusy) return;
              setSignupBusy(true);
              try {
                const r = await api.post("/auth/register", {
                  email: signup.email,
                  password: signup.password,
                  name: signup.name,
                  role: "client",
                });
                try {
                  window.localStorage.setItem("sasha_token", r.data.token);
                } catch {
                  /* storage unavailable */
                }
                toast.success("Welcome to Janté.");
                await refresh();
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Could not create account.");
              } finally {
                setSignupBusy(false);
              }
            }}
            className="lux-card flex flex-col gap-4"
            data-testid="free-signup-form"
          >
            <div className="overline">Create your free account</div>
            <h3 className="font-display text-2xl text-[var(--emerald)]">Two minutes. No card required.</h3>

            <div>
              <div className="overline mb-1">Name</div>
              <input
                className="lux-input"
                required
                value={signup.name}
                onChange={(e) => setSignup({ ...signup, name: e.target.value })}
                data-testid="free-signup-name"
              />
            </div>
            <div>
              <div className="overline mb-1">Email</div>
              <input
                type="email"
                required
                className="lux-input"
                value={signup.email}
                onChange={(e) => setSignup({ ...signup, email: e.target.value })}
                data-testid="free-signup-email"
              />
            </div>
            <div>
              <div className="overline mb-1">Password</div>
              <input
                type="password"
                required
                minLength={6}
                className="lux-input"
                value={signup.password}
                onChange={(e) => setSignup({ ...signup, password: e.target.value })}
                placeholder="Minimum 6 characters"
                data-testid="free-signup-password"
              />
            </div>

            <button
              type="submit"
              disabled={signupBusy}
              className="btn-primary mt-2"
              data-testid="free-signup-submit"
            >
              {signupBusy ? "Creating…" : "Create my free account"}
              <ArrowRight size={14} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] hover:text-[var(--emerald)] mt-1 self-start"
              data-testid="free-signup-have-account"
            >
              I already have an account →
            </button>
          </form>
        </section>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mt-16">
        {tiers.map((t) => {
          const isPlatinum = t.id === "platinum";
          const isCrown = t.id === "crown";
          const comingSoon = isPlatinum || isCrown;
          const isFree = t.id === "free";
          const showStrike = t.intro_price !== null && t.intro_price !== undefined;
          return (
            <div
              key={t.id}
              className={`relative ${isPlatinum ? "lux-card-dark" : "lux-card"} flex flex-col`}
              data-testid={`tier-card-${t.id}`}
            >
              {comingSoon && (
                <div className="absolute -top-3 right-6 z-10">
                  <span
                    className="discount-tag !bg-[var(--brand-red)] !text-[var(--ivory)]"
                    data-testid={`tier-coming-soon-${t.id}`}
                  >
                    Coming soon
                  </span>
                </div>
              )}
              {t.intro_banner && (
                <div className="absolute -top-3 left-6">
                  <span
                    className={`discount-tag ${
                      isPlatinum ? "bg-[var(--gold)] text-[var(--emerald)]" : ""
                    } ${isFree ? "!bg-[var(--emerald)]" : ""}`}
                    data-testid={`tier-banner-${t.id}`}
                  >
                    {t.intro_banner}
                  </span>
                </div>
              )}

              <div className="overline" style={{ fontSize: "0.75rem", letterSpacing: "0.16em" }}>{t.tagline}</div>
              <div className="flex items-center gap-2 mt-3">
                {isPlatinum && <Crown size={20} strokeWidth={1.25} className="text-[var(--gold)]" />}
                {t.id === "joiner" && <Star size={18} strokeWidth={1.25} className="text-[var(--gold)]" />}
                <h3
                  className={`font-display text-3xl ${
                    isPlatinum ? "text-[var(--gold)]" : "text-[var(--emerald)]"
                  }`}
                >
                  {t.name}
                </h3>
              </div>

              <div className="mt-6">
                {t.scheduler_url ? (
                  <div>
                    <div
                      className={`text-[11px] uppercase tracking-[0.22em] ${
                        isPlatinum ? "text-[var(--gold)]" : "text-[var(--brand-red)]"
                      }`}
                      data-testid={`tier-appointment-only-${t.id}`}
                    >
                      Enroll by appointment only
                    </div>
                    <p
                      className={`mt-2 text-sm ${
                        isPlatinum ? "opacity-85" : "text-[var(--ink-2)]"
                      }`}
                      style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "1.1rem" }}
                    >
                      A Janté director will walk you through the program in a private 1:1 review.
                    </p>
                  </div>
                ) : (
                  <>
                    {showStrike && (
                      <div
                        className={`text-base line-through ${
                          isPlatinum ? "opacity-60" : "text-[var(--muted)]"
                        }`}
                        data-testid={`tier-strike-${t.id}`}
                      >
                        ${t.price.toLocaleString()}
                      </div>
                    )}
                    <div className="flex flex-col">
                      <span
                        className={`font-display text-4xl ${
                          isPlatinum ? "text-[var(--ivory)]" : "text-[var(--emerald)]"
                        }`}
                        data-testid={`tier-price-${t.id}`}
                      >
                        {isFree
                          ? "$0"
                          : `$${(showStrike ? t.intro_price : t.price).toLocaleString()}`}
                      </span>
                      <span
                        className={`uppercase mt-1 ${
                          isPlatinum ? "opacity-80" : "text-[var(--ink-2)]"
                        }`}
                        style={{ fontSize: "0.7rem", letterSpacing: "0.16em" }}
                      >
                        {isFree ? t.billing : `/${t.billing}`}
                      </span>
                    </div>
                  </>
                )}
              </div>

              <ul className="mt-6 space-y-3 flex-1">
                {t.features.map((f) => {
                  const isInheritance = f.toLowerCase().startsWith("plus everything in");
                  return (
                    <li
                      key={f}
                      className={`flex items-start gap-2 text-sm ${
                        isPlatinum ? "opacity-90" : "text-[var(--ink-2)]"
                      } ${isInheritance ? "italic font-medium" : ""}`}
                    >
                      <Check
                        size={16}
                        strokeWidth={1.5}
                        className={`shrink-0 mt-0.5 ${
                          isInheritance
                            ? "text-[var(--brand-red)]"
                            : isPlatinum
                            ? "text-[var(--gold)]"
                            : "text-[var(--emerald)]"
                        }`}
                      />
                      <span
                        className={
                          isInheritance
                            ? isPlatinum
                              ? "text-[var(--gold)]"
                              : "text-[var(--brand-red)]"
                            : ""
                        }
                      >
                        {f}
                      </span>
                    </li>
                  );
                })}
              </ul>

              <button
                onClick={() => checkout(t)}
                disabled={loadingTier === t.id}
                className={`mt-8 w-full ${
                  isPlatinum
                    ? "px-8 py-4 text-xs uppercase tracking-[0.18em] border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--emerald)] transition-all duration-300"
                    : isFree
                    ? "btn-gold"
                    : "btn-primary"
                }`}
                data-testid={`tier-checkout-${t.id}`}
              >
                {loadingTier === t.id
                  ? "Redirecting…"
                  : t.scheduler_url
                  ? "Enroll by appointment"
                  : isFree
                  ? "Begin free with Sasha"
                  : `Choose ${t.name}`}
              </button>
            </div>
          );
        })}
      </div>

      <p className="text-center text-xs uppercase tracking-[0.2em] text-[var(--muted)] mt-16">
        Secure checkout · HubSpot Payments · Cancel anytime
      </p>

      {/* Travel preferences — only visible to paid members */}
      <TravelPreferencesSection user={user} />
    </div>
  );
}
